// src/context/CartContext.jsx

import { createContext, useContext, useReducer, useState } from 'react';
import Toast from '../components/Toast'; // يجب أن يكون هذا المسار صحيحًا لمكون Toast.jsx

// Cart Context
const CartContext = createContext();

// Cart Actions
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
};

// Cart Reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM: {
      const existingItem = state.items.find((item) => item.id === action.payload.id);

      if (existingItem) {
        return {
          ...state,
          items: state.items.map((item) =>
            item.id === action.payload.id ? { ...item, quantity: item.quantity + 1 } : item
          ),
        };
      } else {
        return {
          ...state,
          items: [...state.items, { ...action.payload, quantity: 1 }],
        };
      }
    }

    case CART_ACTIONS.REMOVE_ITEM: {
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
      };
    }

    case CART_ACTIONS.UPDATE_QUANTITY: {
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter((item) => item.id !== action.payload.id),
        };
      }

      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
    }

    case CART_ACTIONS.CLEAR_CART: {
      return {
        ...state,
        items: [],
      };
    }

    default:
      return state;
  }
};

// Initial state
const initialState = {
  items: [],
};

// Cart Provider Component
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [toast, setToast] = useState(null); // حالة لإدارة رسالة التأكيد

  // Cart functions
  const addToCart = (product) => {
    dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: product });
    setToast({ message: `${product.name} تمت إضافته إلى السلة بنجاح!`, type: 'success' });
    setTimeout(() => setToast(null), 3000); // إخفاء الرسالة بعد 3 ثوانٍ
  };

  const removeFromCart = (productId) => {
    const product = state.items.find((item) => item.id === productId);
    dispatch({ type: CART_ACTIONS.REMOVE_ITEM, payload: productId });
    if (product) {
      setToast({ message: `${product.name} تم حذفه من السلة.`, type: 'info' });
      setTimeout(() => setToast(null), 3000); // إخفاء الرسالة بعد 3 ثوانٍ
    }
  };

  const updateQuantity = (productId, quantity) => {
    const product = state.items.find((item) => item.id === productId);
    dispatch({
      type: CART_ACTIONS.UPDATE_QUANTITY,
      payload: { id: productId, quantity },
    });
    if (product && quantity > 0) {
      setToast({ message: `تم تحديث كمية ${product.name} إلى ${quantity}.`, type: 'info' });
      setTimeout(() => setToast(null), 3000); // إخفاء الرسالة بعد 3 ثوانٍ
    }
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
    setToast({ message: 'تم إفراغ سلة التسوق.', type: 'info' });
    setTimeout(() => setToast(null), 3000); // إخفاء الرسالة بعد 3 ثوانٍ
  };

  // Calculated values
  const totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);

  const totalPrice = state.items.reduce((sum, item) => {
    const price = parseFloat(item.price.replace(' ر.س', ''));
    return sum + price * item.quantity;
  }, 0);

  const value = {
    items: state.items,
    totalItems,
    totalPrice,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
      {toast && <Toast message={toast.message} type={toast.type} />}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
